import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import ProfileIcon from 'Components/Icons/ProfileIcon';
import { v4 as uuidv4 } from 'uuid';
import {
    Avatar,
    Box,
    Chip,
    ClickAwayListener,
    Divider,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Popover,
    Stack,
    Typography
} from '@mui/material';
import MainCard from 'ui-component/cards/MainCard';
import UpgradePlanCard from './UpgradePlanCard';
import { IconLogout, IconSettings } from '@tabler/icons';
import PerfectScrollbar from 'react-perfect-scrollbar';
import companyDummyImage from 'assets/images/users/Company logo.svg';
import { setIsLoggedIn } from 'store/Authentication/actions';
import { CLEAR_DATA, SET_LOADING, SET_MODAL_STATUS, SET_MODAL_TYPE } from 'store/actions';
import { setCurrentPage } from 'store/ExecutiveTeam/actions';
import { useMutation, useQuery } from '@apollo/client';
import { SELECT_COMPANY_LOGIN, UPDATE_FIREBASE_TOKEN, USER_LOGOUT } from 'schemas/mutation';
import FingerPrintHash from 'Components/FingerPrint';
import UserDefaultImage from 'assets/images/dashboard/default-user.png';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import { getFirebaseToken } from 'Components/Firebase';
import {
    setAllBoardItems,
    setDraftedCurrentPage,
    setDraftedItemsPerPage,
    setDraftedTotalItems,
    setDraftedTotalPages,
    setOutstandingCurrentPage,
    setOutstandingItemsPerPage,
    setOutstandingTotalItems,
    setOutstandingTotalPages,
    setResolvedCurrentPage,
    setResolvedItemsPerPage,
    setResolvedTotalItems,
    setResolvedTotalPages
} from 'store/Questions/actions';
import { KanbanBoardStatus } from 'views/pages/dashboard/KanbanQuestions/constants';
import { GET_FILE_URL } from 'schemas/query';
const ProfileSection = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const [url, setUrl] = useState('');

    const customization = useSelector((state) => state.customization);
    const authentication = useSelector((state) => state.authentication);
    const { data } = useQuery(GET_FILE_URL, {
        fetchPolicy: 'cache-first',
        variables: {
            key: customization.companyData.companyLogo
        },
        skip: !customization.companyData.companyLogo
    });

    let { companyList, companyCreator, hasSubscribed, companyValidated, permissions } = authentication;
    let disableProfile = true;
    if (companyCreator && hasSubscribed && companyValidated) {
        disableProfile = false;
    }
    if (permissions && permissions.hasAdministrativeControl) {
        disableProfile = false;
    }
    companyList = companyList.filter(function (value, index, array) {
        return value._id !== customization.companyData._id;
    });
    var day = new Date();
    var greetings = '';
    var hr = day.getHours();
    if (hr >= 0 && hr < 12) {
        greetings = 'Good Morning';
    } else if (hr == 12) {
        greetings = 'Good Noon';
    } else if (hr >= 12 && hr <= 16) {
        greetings = 'Good Afternoon';
    } else {
        greetings = 'Good Evening';
    }

    const navigate = useNavigate();
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [selectCompanyLogin] = useMutation(SELECT_COMPANY_LOGIN);
    const [updateFirebaseToken] = useMutation(UPDATE_FIREBASE_TOKEN);

    const [userLogout] = useMutation(USER_LOGOUT);

    function updateFirebaseData(companyId, fingerprint) {
        getFirebaseToken().then((data) => {
            if (data && data != '') {
                updateFirebaseToken({
                    variables: {
                        fingerprint: fingerprint,
                        companyId: companyId,
                        firebaseToken: data
                    }
                });
            }
        });
    }

    useEffect(() => {
        if (data) {
            setUrl(data?.getSignedUrlFromKey?.signedUrl || '');
        }
    }, [data]);

    const resetState = () => {
        dispatch({
            type: setAllBoardItems,
            boardItems: {
                [uuidv4()]: {
                    title: KanbanBoardStatus.outstanding,
                    items: []
                },
                [uuidv4()]: {
                    title: KanbanBoardStatus.drafted,
                    items: []
                },
                [uuidv4()]: {
                    title: KanbanBoardStatus.resolved,
                    items: []
                }
            }
        });
        dispatch({ type: setOutstandingTotalItems, outstandingTotalItems: 0 });
        dispatch({ type: setOutstandingTotalPages, outstandingTotalPages: 0 });
        dispatch({ type: setDraftedTotalItems, draftedTotalItems: 0 });
        dispatch({ type: setDraftedTotalPages, draftedTotalPages: 0 });
        dispatch({ type: setResolvedTotalItems, resolvedTotalItems: 0 });
        dispatch({ type: setResolvedTotalPages, resolvedTotalPages: 0 });
        dispatch({ type: setResolvedCurrentPage, resolvedCurrentPage: 1 });
        dispatch({ type: setResolvedItemsPerPage, resolvedItemsPerPage: 1 });
        dispatch({ type: setOutstandingCurrentPage, outstandingCurrentPage: 1 });
        dispatch({ type: setOutstandingItemsPerPage, outstandingItemsPerPage: 6 });
        dispatch({ type: setDraftedCurrentPage, draftedCurrentPage: 6 });
        dispatch({ type: setDraftedItemsPerPage, draftedItemsPerPage: 6 });
    };
    const handleLogout = async () => {
        if (confirm('Are you sure you want to logout?')) {
            const fingerprint = await FingerPrintHash();
            const { data } = await userLogout({
                variables: {
                    logoutUserInput: {
                        fingerprint: fingerprint
                    }
                }
            });
            if (data.userLogout) {
                localStorage.removeItem('diologAuthToken');
                localStorage.removeItem('diologRefToken');
                localStorage.removeItem('diologUserData');
                localStorage.clear();
                dispatch({ type: CLEAR_DATA });
                resetState();
                dispatch({ type: setIsLoggedIn, isLoggedIn: false });
                navigate('/');
            }
        }
    };

    const onSwitchCompanyChange = async (companyId) => {
        dispatch({
            type: SET_LOADING,
            loading: true
        });

        const fingerprint = await FingerPrintHash();
        try {
            const { data } = await selectCompanyLogin({
                variables: {
                    companyId: companyId,
                    fingerprint: fingerprint
                }
            });

            if (data && data.selectCompanyForLogin.userData) {
                updateFirebaseData(companyId, fingerprint);
                const { token, user, isInviteTeamSkipped } = data.selectCompanyForLogin.userData;
                let parsedUser = user;
                parsedUser.companyId = companyId;
                parsedUser.isInviteTeamSkipped = isInviteTeamSkipped;
                localStorage.setItem('diologUserData', JSON.stringify(parsedUser));
                localStorage.setItem('diologAuthToken', token.accessToken);
                localStorage.setItem('diologRefToken', token.refreshToken);
                navigate('/dashboard/company-profile/company-details');
                window.location.reload();
            }
        } catch (e) {
            dispatch({
                type: SET_TOAST_OBJECT,
                toastObject: {
                    status: true,
                    variant: 'error',
                    message: e.message
                }
            });
        } finally {
            dispatch({
                type: SET_LOADING,
                loading: false
            });
        }
    };

    const anchorRef = useRef(null);
    const changeCompanyHandler = (companySelectedData) => {
        handleClose();
        if (companySelectedData.isLocked) {
            dispatch({
                type: SET_MODAL_STATUS,
                modalStatus: true
            });
            dispatch({
                type: SET_MODAL_TYPE,
                modalType: 'lost_access'
            });
            return;
        }
        // const userData = localStorage.getItem('diologUserData');
        // const userDataParsed = JSON.parse(userData);
        // userDataParsed.companyId = companySelectedData._id;
        // localStorage.setItem('diologUserData', JSON.stringify(userDataParsed));
        onSwitchCompanyChange(companySelectedData._id);
    };
    const handleListItemClick = (event, index, route = '') => {
        setSelectedIndex(index);
        handleClose(event);
        if (route && route !== '') {
            navigate(route);
        }
    };
    const [anchorEl, setAnchorEl] = useState(null);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const open = Boolean(anchorEl);
    const id = open ? 'history-popover' : undefined;

    return (
        <>
            <Chip
                disableRipple
                sx={{
                    // height: '52px',
                    // width: '103px',
                    // alignItems: 'center',
                    // borderRadius: '27px',
                    // transition: 'all .2s ease-in-out',
                    // borderColor: theme.palette.orange.light,
                    // backgroundColor: theme.palette.white.light,
                    '&[aria-controls="menu-list-grow"], &:hover': {
                        // borderColor: theme.palette.orange.main,
                        // background: `${theme.palette.orange.main}!important`,
                        background: 'transparent !important',
                        // color: theme.palette.white.light,
                        '& svg': {
                            // stroke: theme.palette.white.light
                        }
                    },

                    '&[aria-controls="menu-list-grow"]': {
                        '& svg': {
                            transform: 'rotate(180deg)'
                        }
                    }
                    // '& .MuiChip-label': {
                    //     lineHeight: 0
                    // }
                }}
                avatar={
                    <Avatar
                        src={customization?.userProfile || UserDefaultImage}
                        sx={{
                            ...theme.typography.mediumAvatar,
                            cursor: 'pointer'
                        }}
                        ref={anchorRef}
                        aria-controls={open ? 'menu-list-grow' : undefined}
                        aria-haspopup="true"
                        color="inherit"
                        className="avatar-image"
                    />
                }
                // label={<IconSettings stroke={1.5} size="1.625rem" color={theme.palette.orange.light} />}
                label={<KeyboardArrowDownRoundedIcon />}
                variant="outlined"
                ref={anchorRef}
                aria-controls={open ? 'menu-list-grow' : undefined}
                aria-haspopup="true"
                onClick={handleClick}
                color="primary"
                className={customization.activePage === 'profile' ? 'header-profie-card active' : 'header-profie-card'}
            />
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
            >
                <Paper>
                    <ClickAwayListener onClickAway={handleClose}>
                        <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]} className="user-box">
                            <PerfectScrollbar
                                style={{ height: '100%', maxHeight: 'calc(100vh - 100px)', overflowX: 'hidden', overflowY: 'hidden' }}
                            >
                                <Box sx={{ p: 2, pt: 3 }}>
                                    <Stack>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <Typography variant="h4">{greetings},</Typography>
                                            {customization.userData.userName ||
                                                (customization?.userData.firstName && (
                                                    <Typography component="span" sx={{ fontWeight: 400 }}>
                                                        {customization?.userData?.userName ||
                                                            `${customization?.userData?.firstName} ${
                                                                customization?.userData?.lastName || ''
                                                            }`}
                                                    </Typography>
                                                ))}
                                        </Stack>
                                        {customization.userData.jobTitle && (
                                            <Typography variant="body2">{customization.userData.jobTitle}</Typography>
                                        )}
                                    </Stack>
                                </Box>
                                <Divider />
                                <List
                                    component="nav"
                                    sx={{
                                        width: '100%',
                                        maxWidth: 350,
                                        minWidth: 300,
                                        backgroundColor: theme.palette.background.paper,
                                        [theme.breakpoints.down('md')]: {
                                            minWidth: '100%'
                                        },
                                        '& .MuiListItemButton-root': {
                                            p: 1,
                                            pl: 2
                                        }
                                    }}
                                >
                                    {customization.companyData && (
                                        <ListItemButton
                                            sx={{ borderRadius: `${customization.borderRadius}px`, p: '0', m: '0' }}
                                            selected={selectedIndex === 0}
                                            disabled={disableProfile}
                                            className={disableProfile ? 'opacity-1' : ''}
                                            onClick={(event) => {
                                                handleListItemClick(event, 0, '/dashboard/company-profile/company-details');
                                                dispatch({ type: setCurrentPage, currentPage: 'change-details' });
                                            }}
                                        >
                                            <Avatar
                                                alt={customization.companyData ? customization.companyData.legalBusinessName : ''}
                                                src={url ? url : companyDummyImage}
                                                sx={{ width: 48, height: 48, mr: 2 }}
                                            />
                                            <ListItemText
                                                primary={
                                                    <Typography variant="h5" component="h5">
                                                        {customization.companyData.legalBusinessName}
                                                    </Typography>
                                                }
                                            />
                                            {!disableProfile && (
                                                <ListItemIcon>
                                                    <IconSettings stroke={1.5} size="1.5rem" className="icon-orange" />
                                                </ListItemIcon>
                                            )}
                                        </ListItemButton>
                                    )}
                                </List>
                                <Divider />
                                <Box>
                                    {companyCreator && <UpgradePlanCard />}
                                    <Divider />
                                    {/* Another Company Code */}
                                    {companyList
                                        ? companyList.map((data) => (
                                              <List
                                                  sx={{
                                                      width: '100%',
                                                      maxWidth: 350,
                                                      minWidth: 300,
                                                      backgroundColor: theme.palette.background.paper,
                                                      borderRadius: '10px',
                                                      [theme.breakpoints.down('md')]: {
                                                          minWidth: '100%'
                                                      },
                                                      '& .MuiListItemButton-root': {
                                                          px: 1,
                                                          py: 1.5
                                                      }
                                                  }}
                                                  className="disabled"
                                                  key={data._id}
                                              >
                                                  {customization.companyData && (
                                                      <ListItemButton
                                                          sx={{ borderRadius: `${customization.borderRadius}px`, p: '0', m: '0' }}
                                                          selected={selectedIndex === 0}
                                                          onClick={() => changeCompanyHandler(data)}
                                                      >
                                                          <Avatar
                                                              src={data.companyLogo ? data.companyLogo : companyDummyImage}
                                                              sx={{ width: 48, height: 48, mr: 2, background: '#fc7013' }}
                                                          />
                                                          <ListItemText
                                                              primary={
                                                                  <Typography variant="h5" component="h5">
                                                                      {data.legalBusinessName}
                                                                  </Typography>
                                                              }
                                                          />
                                                          <ListItemIcon>
                                                              <IconSettings stroke={1.5} size="1.5rem" className="icon-orange" />
                                                          </ListItemIcon>
                                                      </ListItemButton>
                                                  )}
                                                  <Divider />
                                              </List>
                                          ))
                                        : ''}

                                    <List className="profile-sub-menu">
                                        {hasSubscribed && (
                                            <ListItemButton
                                                // sx={{ borderRadius: `${customization.borderRadius}px` }}
                                                selected={selectedIndex === 4}
                                                onClick={(event) => handleListItemClick(event, 0, '/dashboard/profile')}
                                            >
                                                <ListItemIcon>
                                                    <ProfileIcon className="icon-primary-fill" size="1.5rem" />
                                                </ListItemIcon>
                                                <ListItemText primary={<Typography variant="body3">Profile</Typography>} />
                                            </ListItemButton>
                                        )}
                                        <Divider />
                                        <ListItemButton
                                            // sx={{ borderRadius: `${customization.borderRadius}px` }}
                                            selected={selectedIndex === 4}
                                            onClick={handleLogout}
                                        >
                                            <ListItemIcon>
                                                <IconLogout className="icon-primary" stroke={1.5} size="1.5rem" />
                                            </ListItemIcon>
                                            <ListItemText primary={<Typography variant="body3">Logout</Typography>} />
                                        </ListItemButton>
                                    </List>
                                </Box>
                            </PerfectScrollbar>
                        </MainCard>
                    </ClickAwayListener>
                </Paper>
            </Popover>
        </>
    );
};

export default ProfileSection;
